/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useEffect, useCallback} from "react"
import SideNavigation from "../../shared/SideNavBar"
import TopNavigation from "../../shared/TopNavBar"
import Routes from "../../Routes"
import UserSettings from "../../components/UserSettings"
import {AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal} from "@azure/msal-react"
import {authSettings} from "../../Config"
import {MDBSpinner} from "mdbreact"
import {InteractionRequiredAuthError, InteractionStatus} from "@azure/msal-browser"
import {useLocation} from "react-router-dom"
import {useAppDispatch, useAppSelector} from "../../store"
import {RootState} from "../../rootReducer"
import {
    loadingAccessTokenAction,
    setAccessTokenAction,
    setUserInformationDataAction,
    getUserRegionsAction,
    getUserDealersAction,
    getUserAllRegionsAndDealers,
    fetchSchedulesForValidation,
    getAccessPermission,
} from "./appSlice"

//kendo intl
import likelySubtags from "cldr-core/supplemental/likelySubtags.json"
import currencyData from "cldr-core/supplemental/currencyData.json"
import weekData from "cldr-core/supplemental/weekData.json"
import gbNumbers from "cldr-numbers-full/main/en-GB/numbers.json"
import gbLocalCurrency from "cldr-numbers-full/main/en-GB/currencies.json"
import gbCaGregorian from "cldr-dates-full/main/en-GB/ca-gregorian.json"
import gbDateFields from "cldr-dates-full/main/en-GB/dateFields.json"
import {IntlProvider, load, LocalizationProvider} from "@progress/kendo-react-intl"
import {
    fetchMakeData,
    fetchVehicleClassesData,
    fetchVehicleTypesData,
    fetchBookingStatus,
    fetchAxles,
    fetchTests,
    fetchTestStations,
    fetchTransactionTypes,
} from "../MotBooking/bookingSlice"
import {UserRegion} from "./models"
import BreakAddEditModal from "../../components/MotBooking/BreakAddEditModal"
import TestBookingAddEditModal from "../../components/MotBooking/TestBookingAddEditModal"
import MOTOpportunityAddEditModal from "../../components/Activity/MOTOpportunityAddEditModal"
import {getAccessPolicies} from "../AccessPolicies/accessPoliciesSlice"
import {fetchAllRoles} from "../User/userSlice"
import {ApplicationInsights} from "@microsoft/applicationinsights-web"

export const appInsights = new ApplicationInsights({
    config: {
        instrumentationKey: process.env.REACT_APP_AI_INSTRUMENTATION_KEY,
        /* ...Other Configuration Options... */
    },
})
appInsights.loadAppInsights()

load(likelySubtags, currencyData, weekData, gbNumbers, gbLocalCurrency, gbCaGregorian, gbDateFields)

const App = () => {
    const dispatch = useAppDispatch()
    const accessToken = useAppSelector((state: RootState) => state.application.accessToken)
    const userInfo = useAppSelector((state: RootState) => state.application.userInfo)
    const userRegions = useAppSelector((state: RootState) => state.application.userRegions)
    const selectedRegion = useAppSelector((state: RootState) => state.application.selectedRegion)
    const defaultRegionSelected = useAppSelector((state: RootState) => state.application.defaultRegionSelected)
    const appRoles = useAppSelector((state: RootState) => state.containers.users.roles)
    const permissions = useAppSelector((state: RootState) => state.application.permissions)
    const userInfoRoleId = useAppSelector((state: RootState) => state.application.permissions?.roleId)

    const selectedDealer = useAppSelector((state: RootState) => state.application.selectedSingleDealer?.dealerId)

    const schedulesData = useAppSelector((state: RootState) => state.containers.schedules.schedules)
    const errorList = useAppSelector((state: RootState) => state.application.errorList)

    const breakWidth = 992
    const [locale] = useState("en-GB")

    const [windowWidth, setWindowWidth] = useState(0)
    const [showSideNav, setShowSideNav] = useState(true)
    const isAuthenticated = useIsAuthenticated()
    const {inProgress, instance, accounts} = useMsal()

    const [toggleSettingsDialog, setToggleSettingsDialog] = useState(false)

    const dynamicMainContent = {
        marginLeft: window.innerWidth > breakWidth && showSideNav ? "240px" : "0",
    }

    const toggleSideNav = () => {
        setShowSideNav(!showSideNav)
    }
    appInsights.trackPageView()
    const sideNavClick = () => {
        if (!(window.innerWidth > 992)) {
            toggleSideNav()
        }
    }

    const handleResize = useCallback(() => {
        setWindowWidth(window.innerWidth)
        setShowSideNav(window.innerWidth > 992)
    }, [windowWidth, showSideNav])

    useEffect(() => {
        if (isAuthenticated && accessToken === "") {
            getToken()
        }
    }, [isAuthenticated, accessToken])

    useEffect(() => {
        if (isAuthenticated && selectedRegion.regionId > 0) {
            dispatch(fetchAllRoles())
            dispatch(getAccessPermission())
            dispatch(getAccessPolicies())
            dispatch(fetchMakeData())
            dispatch(fetchVehicleClassesData())
            dispatch(fetchVehicleTypesData())
            dispatch(fetchTestStations())
            dispatch(fetchBookingStatus())
            dispatch(fetchTransactionTypes())
            dispatch(fetchAxles())
            dispatch(fetchTests())
            dispatch(getUserAllRegionsAndDealers(userInfo.localAccountId))
        }
    }, [isAuthenticated, selectedRegion])

    useEffect(() => {
        //update Schedules for Vaidation when Schedules are updated.
        if ((selectedDealer && selectedDealer > 0) || (selectedDealer && schedulesData.length > 0))
            dispatch(fetchSchedulesForValidation(selectedDealer.toString()))
    }, [selectedDealer, schedulesData])

    useEffect(() => {
        window.addEventListener("resize", handleResize)
    }, [])

    useEffect(
        () => () => {
            window.removeEventListener("resize", handleResize)
        },
        [],
    )
    useEffect(() => {
        if (defaultRegionSelected) dispatch(getUserDealersAction("SINGLE_DEALER"))
    }, [defaultRegionSelected])

    const handleLogin = (loginType: string) => {
        if (loginType === "popup") {
            instance.loginPopup({scopes: authSettings.auth.scopes}).catch(e => {})
        } else if (loginType === "redirect") {
            instance
                .loginRedirect({scopes: authSettings.auth.scopes})
                .catch(e => {})
                .then(e => {})
        }
    }

    const handleLogout = (logoutType: string) => {
        if (logoutType === "popup") {
            instance.logoutPopup()
        } else if (logoutType === "redirect") {
            instance.logoutRedirect()
        }
    }

    const getToken = () => {
        dispatch(loadingAccessTokenAction(true))
        instance
            .acquireTokenSilent({
                scopes: authSettings.auth.scopes,
                account: accounts[0],
                forceRefresh: true,
            })
            .then(response => {
                dispatch(loadingAccessTokenAction(false))
                dispatch(setAccessTokenAction(response.accessToken))
                dispatch(setUserInformationDataAction(response.account))
                dispatch(getUserRegionsAction())
            })
            .catch(error => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect({
                        scopes: authSettings.auth.scopes,
                        loginHint: accounts[0].username,
                    })
                }
            })
    }

    const location = useLocation()

    const signOutPage = () => (
        <div className="mdb-container flex-center flex-column">
            <div className="text-center">
                <h1 className="display-4">Signed out</h1>
                <p className="text-input sub-title">You have been signout successfully</p>
                <a className="btn btn-dark" href="/">
                    Login
                </a>
            </div>
        </div>
    )

    if (location.pathname !== "/signout" && inProgress === InteractionStatus.None && !isAuthenticated) {
        handleLogin("redirect")
    }

    return (
        <LocalizationProvider language="en-GB">
            <IntlProvider locale={locale}>
                <div className="app">
                    <>
                        {location.pathname === "/signout" && (
                            <UnauthenticatedTemplate>{signOutPage()}</UnauthenticatedTemplate>
                        )}
                        <AuthenticatedTemplate>
                            {isAuthenticated && userInfo != null && defaultRegionSelected && permissions && appRoles ? (
                                <>
                                    <div className="white-skin">
                                        <SideNavigation triggerOpening={showSideNav} onLinkClick={sideNavClick} />
                                    </div>
                                    <div className="flexible-content white-skin">
                                        <TopNavigation
                                            toggle={showSideNav}
                                            onSideNavToggleClick={toggleSideNav}
                                            userInfo={userInfo}
                                            selectedRegion={selectedRegion}
                                            openSettingsDialog={setToggleSettingsDialog}
                                        />
                                        <main
                                            style={{
                                                ...dynamicMainContent,
                                                transition: "all .3s",
                                                marginTop: "108px",
                                                padding: ".75rem",
                                                overflowY: "auto",
                                                maxHeight: "calc(100vh - 108px)",
                                            }}
                                        >
                                            <Routes userInfoRoleId={userInfoRoleId} />
                                            <TestBookingAddEditModal />
                                            <BreakAddEditModal />
                                            <MOTOpportunityAddEditModal />
                                            <UserSettings
                                                toggleSettingsDialog={toggleSettingsDialog}
                                                closeSettingsDialog={setToggleSettingsDialog}
                                                userInfo={userInfo}
                                                userRegions={userRegions}
                                                selectedRegion={selectedRegion ?? ({} as UserRegion)}
                                                signOutUser={handleLogout}
                                            ></UserSettings>
                                        </main>
                                    </div>
                                </>
                            ) : (
                                <div>
                                    {errorList.length <= 0 ? (
                                        <div
                                            className="d-flex justify-content-center full-height"
                                            style={{alignItems: "center"}}
                                        >
                                            <MDBSpinner style={{width: "8rem", height: "8rem"}} role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </MDBSpinner>
                                        </div>
                                    ) : (
                                        <div className="justify-content p-3 ">
                                            <h4 className="pb-2">Following things must have gone wrong </h4>
                                            {errorList.map(a => {
                                                return (
                                                    <ul>
                                                        <li>
                                                            <span>
                                                                <span style={{color: "#00aeef"}}>
                                                                    {" "}
                                                                    {`${a.traceId} `}
                                                                </span>
                                                                {`- ${a.message}`}
                                                                {a.statusCode?.toString().length > 0 &&
                                                                    ` (${a.statusCode})`}
                                                            </span>
                                                        </li>
                                                    </ul>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </AuthenticatedTemplate>
                    </>
                </div>
            </IntlProvider>
        </LocalizationProvider>
    )
}

export default App
